// src/components/timeBoxes/TimeBoxDetail/TimeBoxDetailActions.tsx
// TimeBox 詳情操作按鈕：Quick Adjust 時間、Delete

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Trash2, Clock } from '../../../../components/common/icons';
import { DRAG_CONFIG } from '../../../../utils/calendarHelper';

interface TimeBoxDetailActionsProps {
  mode: 'create' | 'edit';
  onDelete?: () => void;
  onTimeAdjust?: (minutes: number) => void;
  onSwitchToEvent?: () => void;
  isSubmitting?: boolean;
  startTime?: Date;
  endTime?: Date;
}

export const TimeBoxDetailActions: React.FC<TimeBoxDetailActionsProps> = ({
  mode,
  onDelete,
  onTimeAdjust,
  onSwitchToEvent,
  isSubmitting = false,
  startTime,
  endTime
}) => {
  const [isQuickAdjustOpen, setIsQuickAdjustOpen] = useState(false);

  // 計算每個調整選項是否合法
  const getAdjustmentValidity = (minutes: number): boolean => {
    if (!startTime || !endTime) return true;

    const newEndTime = new Date(endTime);
    newEndTime.setMinutes(newEndTime.getMinutes() + minutes);

    // 檢查 1: End Time 必須晚於 Start Time
    if (newEndTime <= startTime) return false;

    // 檢查 2: 最小時長 15 分鐘
    const durationInMs = newEndTime.getTime() - startTime.getTime();
    const durationInMinutes = Math.floor(durationInMs / (1000 * 60));
    if (durationInMinutes < DRAG_CONFIG.MIN_DURATION_MINUTES) return false;

    return true;
  };

  const handleQuickAdjust = (minutes: number) => {
    setIsQuickAdjustOpen(false);
    if (onTimeAdjust) {
      onTimeAdjust(minutes);
    }
  };

  return (
    <div className="flex gap-2">
      {/* Quick Adjust Button (僅 edit 模式) */}
      {mode === 'edit' && onTimeAdjust && (
        <div className="flex-1 relative">
          <button
            onClick={() => setIsQuickAdjustOpen(!isQuickAdjustOpen)}
            disabled={isSubmitting}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Clock size={16} />
            <span>Quick Adjust</span>
            <ChevronDown size={16} className={`transition-transform ${isQuickAdjustOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Quick Adjust Menu */}
          <AnimatePresence>
            {isQuickAdjustOpen && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-dropdown-backdrop"
                  onClick={() => setIsQuickAdjustOpen(false)}
                />

                {/* Menu */}
                <motion.div
                  className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-dropdown overflow-hidden"
                  initial={{ opacity: 0, y: 8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                >
                  {/* Extend Options */}
                  <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Extend
                  </div>
                  <button
                    onClick={() => handleQuickAdjust(15)}
                    disabled={!getAdjustmentValidity(15)}
                    className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-between transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
                  >
                    <span>+15 minutes</span>
                  </button>
                  <button
                    onClick={() => handleQuickAdjust(30)}
                    disabled={!getAdjustmentValidity(30)}
                    className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-between transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
                  >
                    <span>+30 minutes</span>
                  </button>

                  <div className="my-1 border-t border-gray-100" />

                  {/* Shorten Options */}
                  <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Shorten
                  </div>
                  <button
                    onClick={() => handleQuickAdjust(-15)}
                    disabled={!getAdjustmentValidity(-15)}
                    className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-between transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
                  >
                    <span>-15 minutes</span>
                  </button>
                  <button
                    onClick={() => handleQuickAdjust(-30)}
                    disabled={!getAdjustmentValidity(-30)}
                    className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-between transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
                  >
                    <span>-30 minutes</span>
                  </button>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Delete Button (僅 edit 模式) */}
      {mode === 'edit' && onDelete && (
        <button
          onClick={onDelete}
          disabled={isSubmitting}
          className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-500 hover:text-red-600 hover:border-red-300 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Delete TimeBox"
        >
          <Trash2 size={18} />
        </button>
      )}

      {/* Switch to Event (僅 create 模式) */}
      {mode === 'create' && onSwitchToEvent && (
        <button
          onClick={onSwitchToEvent}
          disabled={isSubmitting}
          className="w-full inline-flex items-center justify-center px-4 py-2 border border-sky-300 rounded-lg text-sm font-medium text-sky-700 bg-white hover:bg-sky-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Switch to Event
        </button>
      )}
    </div>
  );
};
